// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Resources } from '@shared/localizations/Resources';
import { EventItem } from '../cache/EventItem';
import { ReportMessageTooltip } from '@shared/utils/ReportMessageTooltip';
import { TerceraTicketScreen } from '../screen/TerceraTicketScreen';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { EventType } from '@shared/utils/Event/EventConstants.types';
import { EventUtils } from '@shared/commons/UtilsClasses/EventUtils';
import { EventCache } from '@shared/commons/cache/Event/EventCache';
import { DataCache } from '@shared/commons/DataCache';
import { SessionSettings } from '@shared/commons/SessionSettings';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import EventsPanelTemplate from '@front/templates/EventsPanelTemplate.html';
import { DateTimeRangeOption, DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class EventsPanel extends ApplicationPanelWithTable {
    constructor() {
        super(...arguments);
        this.selectedEventTypes = new Set();
        this.eventTypes = new Set();
        this.startTime = 0;
        this.finishTime = 0;
        this.rangeSelectPanel = null;
        // 'panel.rangeSelect.logs.tooltip': 'Select period for displaying logs'
        this.headerLocaleKey = 'panel.EventLog.NamePanel';
        this.skipOnSave = true;
        this.NeedCalculateRowCount = false;
        this.allowResizers = {
            left: true,
            top: true,
            right: false,
            bottom: false
        };
    }
    get qt() {
        return this.getQuickTable();
    }
    getType() {
        return PanelNames.EventsPanel;
    }
    oncomplete() {
        super.oncomplete();
        this.localize();
        this.rangeSelectPanel = this.Controls.eventsRangeSelect;
        this.rangeSelectPanel.getReportEvent.Subscribe(this.rangeSelected, this);
        this.rangeSelectPanel.updateSelectedRange();
        [EventType.Exception, EventType.Trading, EventType.System].forEach((type) => {
            this.adjustEventFilter(type);
        });
        this.qt.AddToEnd = false;
        this.qt.isMoveColumnAllowed = false;
        this.qt.canShowHeaderMenu = false; // #88411
        this.populateTableContextMenu();
        this.qt.OnPaintedPictureButtonClick.Subscribe(this.getEventTicket, this);
        this.observe('visible', (newValue) => {
            const visibleChangedCallback = this.get('visibleChangedCallback');
            if (!isNullOrUndefined(visibleChangedCallback)) {
                visibleChangedCallback(newValue);
            }
        });
        const evtCache = EventCache;
        evtCache.OnAddEvent.Subscribe(this.OnAddEvent, this);
        evtCache.OnClearEvents.Subscribe(this.OnClearEvents, this);
        this.on('filterShowContextMenu', this.filterShowContextMenuAction);
        const rangeComboBox = this.rangeSelectPanel.Controls.rangeComboBox;
        const comboItems = [
            { type: NewContextMenuItemType.Default, text: DateTimeRangeOption.DAILY, value: DateTimeRangeOption.DAILY },
            { type: NewContextMenuItemType.Default, text: 'panel.rangeSelect.range.all', value: DateTimeRangeOption.D7 }
        ];
        rangeComboBox.clearItems();
        comboItems.forEach((item) => rangeComboBox.addItem(item));
        const rangeTimes = DateTimeUtils.getRangeByOption(comboItems[0].value);
        this.startTime = rangeTimes.startTime;
        this.finishTime = rangeTimes.finishTime;
    }
    dispose() {
        this.OnClose.Raise(this);
        const evtCache = EventCache;
        evtCache.OnAddEvent.UnSubscribe(this.OnAddEvent, this);
        evtCache.OnClearEvents.UnSubscribe(this.OnClearEvents, this);
        this.off('filterShowContextMenu', this.filterShowContextMenuAction);
        this.rangeSelectPanel.getReportEvent.UnSubscribe(this.rangeSelected, this);
        super.dispose();
    }
    /* override base close button dispose process */
    close() {
        this.set('visible', false);
    }
    populateItemsDirect() {
        super.populateItemsDirect();
        void this.updateEventItems();
    }
    updateEventItems() {
        return __awaiter(this, void 0, void 0, function* () {
            const eventArr = yield EventCache.getEventsFromDB();
            this.OnClearEvents();
            eventArr.forEach((event) => {
                this.OnAddEvent(event);
            });
        });
    }
    OnAddEvent(ev) {
        this.adjustEventFilter(ev.EventType);
        if (this.eventFilter(ev)) {
            this.qt.AddItem(new EventItem(ev, SessionSettings, DataCache));
        }
    }
    OnClearEvents() {
        if (!isNullOrUndefined(this.qt)) {
            this.qt.ClearAll();
        }
    }
    getEventTicket(args) {
        const row = args.row;
        if (isNullOrUndefined(row))
            return;
        const event = row.item.ev;
        const ticket = TerceraTicketScreen.TicketFromEvent(event);
        // TODO. UGLY.
        if (event.EventType === EventType.Trading) {
            const account = DataCache.GetAccountByIdOrName(event.Account);
            ReportMessageTooltip.fixEntries(ticket.sortedEntries, SessionSettings, DataCache.getInstrumentByTradable_ID(event.InstrumentTradableID, event.RouteID), account, event.ProductType);
        }
        ReportMessageTooltip.localizeEntries(ticket.sortedEntries);
        TerceraTicketScreen.show(ticket);
    }
    populateTableContextMenu() {
        const items = [
            {
                type: NewContextMenuItemType.Default,
                text: 'panel.EventLog.menu.Clear',
                event: this.onRemoveAll.bind(this),
                enabled: false,
                tag: EventsPanel.REMOVE_ALL
            }
        ];
        this.menuTagDict = createTagDictionary(items);
        this.qt.setTableContextMenuItems(items);
    }
    onRemoveAll() {
        const qtRactive = this.quickTableRactive;
        const qt = !isNullOrUndefined(qtRactive) ? qtRactive.quickTable : null;
        if (isNullOrUndefined(qt)) {
            return;
        }
        qt.ClearRows();
    }
    preparePopup() {
        super.preparePopup();
        this.menuTagDict[EventsPanel.REMOVE_ALL].enabled = this.qt.rowsArray.length > 0;
    }
    adjustEventFilter(eventType) {
        if (!this.eventTypes.has(eventType)) {
            this.selectedEventTypes.add(eventType);
        }
        this.eventTypes.add(eventType);
    }
    eventFilter(event) {
        if (this.startTime < event.timestamp &&
            this.finishTime > event.timestamp &&
            this.selectedEventTypes.has(event.EventType)) {
            return true;
        }
        return false;
    }
    filterShowContextMenuAction(context, index) {
        const hiddenArray = [...this.eventTypes].map((eventType) => ({
            type: NewContextMenuItemType.Default,
            text: EventUtils.getEventTypeStr(eventType),
            canCheck: true,
            checked: this.selectedEventTypes.has(eventType),
            closeOnClick: false,
            tag: eventType,
            event: (item) => {
                this.filterContextMenuItemsSelected(item);
            }
        }));
        const offset = this.find('.js-more-button').getBoundingClientRect();
        const posY = offset.top + window.scrollY + 32 + 1; // +32 - height of combobox, +1 - space btw combobox and menu
        const posX = offset.left + window.scrollX;
        showContextMenu({
            x: posX,
            y: posY,
            items: hiddenArray,
            functions: {
                closeCallback: () => {
                    this.onFilterMenuClosed();
                }
            }
        });
        this.set('isFilterMenuShown', true);
    }
    onFilterMenuClosed() {
        this.set('isFilterMenuShown', false);
    }
    rangeSelected(startTime, finishTime) {
        return __awaiter(this, void 0, void 0, function* () {
            this.startTime = startTime;
            this.finishTime = finishTime;
            void this.updateEventItems();
        });
    }
    filterContextMenuItemsSelected(filterItem) {
        if (filterItem.checked) {
            this.selectedEventTypes.add(filterItem.tag);
        }
        else {
            this.selectedEventTypes.delete(filterItem.tag);
        }
        void this.updateEventItems();
    }
}
EventsPanel.REMOVE_ALL = 'REMOVE_ALL';
ApplicationPanelWithTable.extendWith(EventsPanel, {
    partials: {
        bodyPartial: EventsPanelTemplate
    },
    data: function () {
        return {
            showHeader: true,
            showFooter: false,
            dockablePanel: false,
            attached: false,
            resizable: true,
            width: 700,
            height: 550,
            closeBtnVisible: true,
            movable: false,
            canLinkByAccount: false,
            filterBtnTooltip: Resources.getResource('panel.filterSelect.logs.tooltip'),
            isFilterMenuShown: false
        };
    },
    headerLocaleKey: 'panel.EventLog.NamePanel',
    skipOnSave: true,
    NeedCalculateRowCount: false,
    allowResizers: {
        left: true,
        top: true,
        right: false,
        bottom: false
    },
    rangeSelectPanel: null
});
